// Team canvas (/team-and-agents): a person card dropped outside every team
// frame removes that person from the team (CRUD + confirm standard rule 4:
// removing a member is a sensitive action; audit row "team canvas — Team
// membership").
//
// el.call('confirmTeamLeave', { name, team, onConfirm, onCancel }) opens the
// house confirm (confirmDialog) that names the person and the team. Nothing
// changes before the verb: the page's own leave runs only in onConfirm, and
// every other way out (Cancel, Escape, the backdrop, the X, a programmatic
// close) runs onCancel once, so the page can put the card back.
//
// el.call('runTeamLeave', root, { team, userId, email }) is the leave itself:
// the membership indexes drop the team row at once (the frame redraws), the
// SDK removes the member, and a failure puts this person's rows back and
// resolves false (rule 5: the page then rolls the card back and says so).
// It never throws.

// Fill one {token} with a literal value. A function replacer, so a `$&` or a
// `$$` inside a person or team name is never read as a replacement pattern.
const _teamLeaveFill = (text, token, value) => String(text).replace(token, () => String(value))

// One person's rows in a membership index (`teamsByEmail` /
// `teamsByUserId`), without the given team. Returns the same object when the
// index has no such row.
const _teamLeaveDropRow = (index, key, teamId) => {
  if (!key || !index || !Array.isArray(index[key])) return index
  const rows = index[key].filter((row) => !row || row.id !== teamId)
  if (rows.length === index[key].length) return index
  const next = { ...index }
  if (rows.length) next[key] = rows
  else delete next[key]
  return next
}

// Put one person's rows back as they were in `before` (rollback). Only that
// key changes, so a refresh of anyone else's rows in the meantime survives.
const _teamLeaveRestoreRows = (index, key, before) => {
  if (!key) return index
  const next = { ...(index || {}) }
  if (before && Array.isArray(before[key])) next[key] = before[key]
  else delete next[key]
  return next
}

export const confirmTeamLeave = function (ask) {
  const { name, team, onConfirm, onCancel } = ask || {}
  if (!team || !team.id || typeof onConfirm !== 'function') return
  const fill = (key) =>
    _teamLeaveFill(_teamLeaveFill(this.call('polyglot', key), '{name}', name), '{team}', team.name)
  let confirmed = false
  this.call('confirmDialog', {
    title: fill('canvas.team.leave.title'),
    message: fill('canvas.team.leave.message'),
    confirmText: this.call('polyglot', 'canvas.team.leave.confirm'),
    cancelText: this.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: () => {
      confirmed = true
      onConfirm()
    },
    // onClose runs after every way out, a programmatic closeModal included
    // (onCancel alone misses that one), and after onConfirm too — hence the
    // flag.
    onClose: () => {
      if (!confirmed && typeof onCancel === 'function') onCancel()
    }
  })
}

export const runTeamLeave = async function (root, leave) {
  const { team, userId, email } = leave || {}
  if (!root || !team || !team.id || !userId) return false
  const emailKey = String(email || '').toLowerCase()
  const idKey = String(userId)
  const byEmail = root.teamsByEmail || {}
  const byUserId = root.teamsByUserId || {}
  root.update({
    teamsByEmail: _teamLeaveDropRow(byEmail, emailKey, team.id),
    teamsByUserId: _teamLeaveDropRow(byUserId, idKey, team.id)
  })
  try {
    await this.call('removeMemberFromTeam', root, {
      orgId: team.orgId,
      teamId: team.id,
      userId,
      email
    })
    return true
  } catch (err) {
    root.update({
      teamsByEmail: _teamLeaveRestoreRows(root.teamsByEmail, emailKey, byEmail),
      teamsByUserId: _teamLeaveRestoreRows(root.teamsByUserId, idKey, byUserId)
    })
    console.warn('[team-and-agents] leave-team drop failed:', err?.message || err)
    return false
  }
}
